import { createContext, useContext, useState, useCallback, useLayoutEffect, type ReactNode } from "react";

export interface BreadcrumbItem {
  label: string;
  path?: string; // If undefined, not clickable (last segment)
}

export interface HeaderConfig {
  breadcrumbs: BreadcrumbItem[];
  actions?: ReactNode;
}

interface HeaderContextValue {
  config: HeaderConfig;
  setConfig: (config: HeaderConfig) => void;
}

const defaultConfig: HeaderConfig = {
  breadcrumbs: [],
  actions: undefined,
};

const HeaderContext = createContext<HeaderContextValue | undefined>(undefined);

interface HeaderContextProviderProps {
  children: ReactNode;
}

export function HeaderContextProvider({ children }: HeaderContextProviderProps) {
  const [config, setConfigState] = useState<HeaderConfig>(defaultConfig);

  const setConfig = useCallback((newConfig: HeaderConfig) => {
    setConfigState(newConfig);
  }, []);

  return (
    <HeaderContext.Provider value={{ config, setConfig }}>
      {children}
    </HeaderContext.Provider>
  );
}

/**
 * Hook to get the current header configuration.
 * Used by GlobalHeader to render breadcrumbs and actions.
 */
export function useHeaderContext(): HeaderContextValue {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error("useHeaderContext must be used within HeaderContextProvider");
  }
  return context;
}

/**
 * Hook to set the header configuration for a route.
 * Call this in route components to set breadcrumbs and action buttons.
 *
 * @example
 * useHeaderConfig({
 *   breadcrumbs: [
 *     { label: "All clubs", path: "/clubs" },
 *     { label: "Club Name" }, // Last segment not clickable
 *   ],
 *   actions: <Button onClick={handleClick}>Action</Button>,
 * });
 */
/**
 * Hook to set the header configuration for a route.
 * Call this in route components to set breadcrumbs and action buttons.
 *
 * @param config - The header configuration (breadcrumbs and actions)
 * @param deps - Dependencies array for when to update (similar to useEffect deps)
 *
 * @example
 * useHeaderConfig({
 *   breadcrumbs: [
 *     { label: "All clubs", path: "/clubs" },
 *     { label: club.name }, // Last segment not clickable
 *   ],
 *   actions: <Button onClick={handleClick}>Action</Button>,
 * }, [club.name]);
 */
export function useHeaderConfig(config: HeaderConfig, deps: unknown[] = []): void {
  const { setConfig } = useHeaderContext();

  // Use layout effect to set config before paint
  // This ensures the header renders with correct content on first paint
  useLayoutEffect(() => {
    setConfig(config);

    // Clear config on unmount to prevent stale data
    return () => {
      setConfig(defaultConfig);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
